import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Wrench, BarChart, Download, FileText, Eye, Printer, Package as PackageIcon, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Report, ReportType, WorkflowRequest } from '../../types';
import { useAppContext } from '../../hooks/useAppContext';
import useAppStore from '../../store/useAppStore';
import { EmptyState } from '../../components/common/EmptyState';
import { API_BASE_URL } from '../../config';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/common/Skeleton';
import { Pagination } from '../../components/common/Pagination';
import ModernAdminDashboardScreen from './ModernAdminDashboardScreen';

// Helper function for precise time ago formatting
const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 30) return `منذ بضع ثوان`;

    let interval = seconds / 31536000;
    if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
    interval = seconds / 2592000;
    if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
    interval = seconds / 86400;
    if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
    interval = seconds / 3600;
    if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
    interval = seconds / 60;
    if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
    return `منذ ${Math.floor(seconds)} ثوان`;
};

// Helper component for report type cards
const ReportTypeCard: React.FC<{
    title: string;
    icon: React.ElementType;
    reports: (Report | WorkflowRequest)[];
    color: string;
    reportType?: ReportType;
    viewAllPath?: string;
}> = ({ title, icon: Icon, reports, color, reportType, viewAllPath }) => {
    const { setAllReportsFilters } = useAppStore();
    const navigate = useNavigate();

    const handleViewClick = (item: Report | WorkflowRequest) => {
        if ('employeeName' in item) { // Type guard for Report
            navigate(`/reports/${item.id}`);
        } else {
            navigate(`/workflow/${item.id}`);
        }
    }

    const handleViewAll = () => {
        if (viewAllPath) {
            navigate(viewAllPath);
        } else if (reportType) {
            // Redirect to the most relevant section available for Admins
            if (reportType === ReportType.Project) {
                navigate('/admin-projects');
            } else {
                navigate('/');
            }
        } else {
            navigate('/workflow');
        }
    }

    return (
        <Card interactive className="flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                    <Icon size={20} className={color} /> {title}
                </CardTitle>
                <span className={`text-sm font-bold ${color}`}>{reports.length}</span>
            </CardHeader>
            <CardContent className="flex-1 space-y-1">
                {reports.length > 0 ? (
                    reports.slice(0, 3).map(item => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                            <div>
                                <p className="text-sm font-semibold truncate">{'title' in item ? item.title : `تقرير ${'employeeName' in item ? item.details.projectOwner : ''}`}</p>
                                <p className="text-xs text-slate-500">
                                    {'employeeName' in item ? item.employeeName : (item.stageHistory[0]?.processor || 'N/A')}
                                </p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => handleViewClick(item)}>
                                <Eye size={14} />
                            </Button>
                        </div>
                    ))
                ) : (
                    <p className="text-sm text-center text-slate-500 py-4">لا توجد تقارير جديدة.</p>
                )}
            </CardContent>
            <div className="p-3 border-t border-slate-200 dark:border-slate-600">
                <Button variant="secondary" className="w-full" onClick={handleViewAll}>عرض الكل</Button>
            </div>
        </Card>
    );
};


// Minimal type for showing package items in the dashboard card
interface PackageRequest {
    id: string;
    title?: string;
    employeeName?: string;
    creationDate?: string;
}

// Minimal type for showing purchase items in the dashboard card
interface PurchaseItem {
    id: string;
    invoiceNumber: string;
    vendor?: string;
    employeeName?: string;
    invoiceDate?: string;
}

interface QuotationSummary {
    id: string;
    customer_name: string;
    quote_number: string;
    quote_date: string;
    total_with_tax: number;
    created_at?: string;
}

const ClassicAdminDashboardScreen: React.FC = () => {
    const { t, user } = useAppContext();
    const { reports, requests, printReport } = useAppStore();
    const navigate = useNavigate();

    // Fetch latest package requests to show top 3 in card
    const [packageRequests, setPackageRequests] = useState<PackageRequest[]>([]);
    const [pkgLoading, setPkgLoading] = useState(false);
    const [pkgAnimReady, setPkgAnimReady] = useState(false);
    const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([]);
    const [quotationSummaries, setQuotationSummaries] = useState<QuotationSummary[]>([]);
    const [qsLoading, setQsLoading] = useState(false);

    useEffect(() => {
        const fetchPackages = async () => {
            try {
                setPkgLoading(true);
                const res = await fetch(`${API_BASE_URL}/package-requests`, {
                    headers: user ? { 'X-User-Id': user.id, 'X-User-Role': user.role } : {}
                });
                if (!res.ok) throw new Error(`خطأ في استجابة الخادم: ${res.status}`);
                const data = await res.json().catch(() => []);
                const normalized = Array.isArray(data) ? data.map((p: any) => ({
                    id: p.id,
                    title: p.title,
                    employeeName: p.employeeName,
                    creationDate: p.creationDate
                })) : [];
                setPackageRequests(normalized);
            } catch (err) {
                console.error('Failed to load package requests', err);
                setPackageRequests([]);
            } finally {
                setPkgLoading(false);
            }
        };
        fetchPackages();
    }, [user]);

    useEffect(() => {
        // Load purchases from localStorage for development preview
        try {
            const raw = localStorage.getItem('purchase_archive_items');
            const data = raw ? JSON.parse(raw) : [];
            const normalized: PurchaseItem[] = Array.isArray(data) ? data.map((p: any) => ({
                id: String(p.id),
                invoiceNumber: String(p.invoiceNumber || ''),
                vendor: p.vendor,
                employeeName: p.employeeName,
                invoiceDate: p.invoiceDate,
            })) : [];
            // sort by invoiceDate desc if available
            normalized.sort((a, b) => new Date(b.invoiceDate || 0).getTime() - new Date(a.invoiceDate || 0).getTime());
            setPurchaseItems(normalized);
        } catch (err) {
            setPurchaseItems([]);
        }
    }, []);

    useEffect(() => {
        const fetchQuotations = async () => {
            try {
                setQsLoading(true);
                const res = await fetch(`${API_BASE_URL}/quotations?limit=12`);
                const data = await res.json().catch(() => []);
                const normalized = Array.isArray(data) ? data : (Array.isArray((data as any).items) ? (data as any).items : []);
                const mapped: QuotationSummary[] = normalized.map((q: any) => ({
                    id: String(q.id),
                    customer_name: String(q.customer_name || ''),
                    quote_number: String(q.quote_number || ''),
                    quote_date: String(q.quote_date || ''),
                    total_with_tax: Number(q.total_with_tax || 0),
                    created_at: String(q.created_at || '')
                }));
                setQuotationSummaries(mapped);
            } catch {
                setQuotationSummaries([]);
            } finally {
                setQsLoading(false);
            }
        };
        fetchQuotations();
    }, []);

    useEffect(() => {
        if (!pkgLoading) {
            setPkgAnimReady(false);
            const t = setTimeout(() => setPkgAnimReady(true), 0);
            return () => clearTimeout(t);
        }
    }, [pkgLoading, packageRequests.length]);

    const sortedReports = useMemo(() => {
        return [...reports].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [reports]);

    const reportCategories = useMemo(() => {
        const sales = sortedReports.filter(r => r.type === ReportType.Sales);
        const maintenance = sortedReports.filter(r => r.type === ReportType.Maintenance);
        const projects = sortedReports.filter(r => r.type === ReportType.Project);
        const sortedRequests = [...requests].sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime());

        return {
            sales,
            maintenance,
            projects,
            workflows: sortedRequests
        };
    }, [sortedReports, requests]);

    const recentAllReports = sortedReports.slice(0, 10);

    const recentPackages = useMemo(() => {
        return [...packageRequests]
            .sort((a, b) => new Date(b.creationDate || 0).getTime() - new Date(a.creationDate || 0).getTime())
            .slice(0, 3);
    }, [packageRequests]);

    const recentPurchases = useMemo(() => {
        return [...purchaseItems].slice(0, 3);
    }, [purchaseItems]);

    const recentUnified = useMemo(() => {
        const reportItems = sortedReports.map(r => ({
            id: r.id,
            kind: 'report' as const,
            title: `تقرير ${r.type} - ${r.id}`,
            subtitle: `من ${r.employeeName} (${r.department})`,
            date: (r.modifications && r.modifications.length > 0)
                ? new Date(r.modifications[r.modifications.length - 1].timestamp)
                : new Date(r.date),
            rType: r.type,
        }));
        const workflowItems = requests.map(req => ({
            id: req.id,
            kind: 'workflow' as const,
            title: req.title || `طلب سير عمل ${req.id}`,
            subtitle: (req.stageHistory && req.stageHistory[0]?.processor) ? req.stageHistory[0].processor : 'غير معروف',
            date: new Date(req.creationDate),
        }));
        const packageItems = packageRequests.map(p => ({
            id: p.id,
            kind: 'package' as const,
            title: p.title || `طلب بكج ${p.id}`,
            subtitle: p.employeeName || 'غير معروف',
            date: new Date(p.creationDate || 0),
        }));
        const purchaseUnified = purchaseItems.map(p => ({
            id: p.id,
            kind: 'purchase' as const,
            title: `فاتورة #${p.invoiceNumber}`,
            subtitle: `${p.vendor || 'غير معروف'}${p.employeeName ? ` • ${p.employeeName}` : ''}`,
            date: new Date(p.invoiceDate || 0),
        }));
        const quotationUnified = quotationSummaries.map(q => ({
            id: q.id,
            kind: 'quotation' as const,
            title: q.customer_name || `عرض ${q.id}`,
            subtitle: `${new Date(q.quote_date || q.created_at || 0).toLocaleDateString('ar-SA')} • ${q.quote_number}`,
            date: new Date(q.quote_date || q.created_at || 0),
        }));
        return [...reportItems, ...workflowItems, ...packageItems, ...purchaseUnified, ...quotationUnified]
            .sort((a, b) => b.date.getTime() - a.date.getTime());
    }, [sortedReports, requests, packageRequests, purchaseItems, quotationSummaries]);

    // حالة فلترة قائمة أحدث العناصر
    const [recentFilter, setRecentFilter] = useState<'all' | 'sales' | 'maintenance' | 'projects' | 'workflow' | 'package' | 'purchase' | 'quotation'>('all');
    const recentUnifiedFiltered = useMemo(() => {
        if (recentFilter === 'all') return recentUnified;
        if (recentFilter === 'sales') return recentUnified.filter(item => item.kind === 'report' && 'rType' in item && item.rType === ReportType.Sales);
        if (recentFilter === 'maintenance') return recentUnified.filter(item => item.kind === 'report' && 'rType' in item && item.rType === ReportType.Maintenance);
        if (recentFilter === 'projects') return recentUnified.filter(item => item.kind === 'report' && 'rType' in item && item.rType === ReportType.Project);
        if (recentFilter === 'workflow') return recentUnified.filter(item => item.kind === 'workflow');
        if (recentFilter === 'package') return recentUnified.filter(item => item.kind === 'package');
        if (recentFilter === 'purchase') return recentUnified.filter(item => item.kind === 'purchase');
        if (recentFilter === 'quotation') return recentUnified.filter(item => item.kind === 'quotation');
        return recentUnified;
    }, [recentUnified, recentFilter]);

    const recentCounts = useMemo(() => {
        return {
            all: recentUnified.length,
            sales: recentUnified.filter(i => i.kind === 'report' && 'rType' in i && i.rType === ReportType.Sales).length,
            maintenance: recentUnified.filter(i => i.kind === 'report' && 'rType' in i && i.rType === ReportType.Maintenance).length,
            projects: recentUnified.filter(i => i.kind === 'report' && 'rType' in i && i.rType === ReportType.Project).length,
            workflow: recentUnified.filter(i => i.kind === 'workflow').length,
            package: recentUnified.filter(i => i.kind === 'package').length,
            purchase: recentUnified.filter(i => i.kind === 'purchase').length,
            quotation: recentUnified.filter(i => i.kind === 'quotation').length,
        };
    }, [recentUnified]);

    const [isSwitching, setIsSwitching] = useState(false);
    const [listAnimReady, setListAnimReady] = useState(false);
    const [recentCollapsed, setRecentCollapsed] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    useEffect(() => {
        setIsSwitching(true);
        const t = setTimeout(() => setIsSwitching(false), 180);
        return () => clearTimeout(t);
    }, [recentFilter]);

    useEffect(() => {
        setListAnimReady(false);
        const t2 = setTimeout(() => setListAnimReady(true), 0);
        return () => clearTimeout(t2);
    }, [recentFilter, recentUnifiedFiltered.length, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [recentFilter]);

    const ITEMS_PER_PAGE = 10;
    const totalPages = Math.max(1, Math.ceil(recentUnifiedFiltered.length / ITEMS_PER_PAGE));
    const paginatedUnified = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return recentUnifiedFiltered.slice(start, start + ITEMS_PER_PAGE);
    }, [recentUnifiedFiltered, currentPage]);

    return (
        <div className="space-y-4">
            <h1 className="text-3xl font-bold">{t('adminDashboard')}</h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <ReportTypeCard title="تقارير المبيعات" icon={BarChart} reports={reportCategories.sales} reportType={ReportType.Sales} color="text-report-sales" />
                <ReportTypeCard title="تقارير الصيانة" icon={Wrench} reports={reportCategories.maintenance} reportType={ReportType.Maintenance} color="text-report-maintenance" />
                <ReportTypeCard title="تقارير المشاريع" icon={Briefcase} reports={reportCategories.projects} reportType={ReportType.Project} color="text-report-project" viewAllPath="/admin-projects" />
                <ReportTypeCard title="الاستيراد والتصدير" icon={Download} reports={reportCategories.workflows} color="text-nav-workflow" />
                <Card interactive className="flex flex-col">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <FileText size={20} className="text-report-project" /> عروض السعر
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 space-y-2">
                        {qsLoading ? (
                            <div className="space-y-2">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="w-8 h-8 rounded-full" />
                                            <div className="space-y-2">
                                                <Skeleton className="h-3 w-40" />
                                                <Skeleton className="h-3 w-24" />
                                            </div>
                                        </div>
                                        <Skeleton className="h-8 w-16 rounded-md" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            (quotationSummaries.slice(0, 3).length > 0 ? (
                                quotationSummaries.slice(0, 3).map(q => (
                                    <div key={q.id} className="group flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg gap-3 transition-all duration-200 hover:bg-white dark:hover:bg-slate-700/50 hover:shadow-sm hover:-translate-y-[1px]">
                                        <div className="flex items-center gap-3">
                                            <div className="relative p-2 bg-primary/10 rounded-full text-primary transition-colors duration-200 group-hover:bg-primary/15 group-hover:text-primary group-hover:ring-1 group-hover:ring-primary/30">
                                                <FileText size={20} />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold truncate">{q.customer_name}</p>
                                                <p className="text-xs text-slate-500">{new Date(q.quote_date || q.created_at || 0).toLocaleDateString('ar-SA')} • {q.quote_number}</p>
                                            </div>
                                        </div>
                                        <Button size="sm" variant="ghost" onClick={() => navigate(`/quotations/${q.id}`)}>
                                            <Eye size={14} />
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-center text-slate-500 py-4">لا توجد عروض سعر بعد.</p>
                            ))
                        )}
                    </CardContent>
                    <div className="p-3 border-t border-slate-200 dark:border-slate-600">
                        <Button variant="secondary" className="w-full" onClick={() => navigate('/admin-quotations')}>عرض الكل</Button>
                    </div>
                </Card>
                {/* NEW: إدارة البكجات - show latest 3 */}
                <Card interactive className="flex flex-col">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <PackageIcon size={20} className="text-nav-workflow" /> إدارة البكجات
                        </CardTitle>
                        <span className={`text-sm font-bold text-nav-workflow`}>{packageRequests.length}</span>
                    </CardHeader>
                    <CardContent className="flex-1 space-y-2">
                        {pkgLoading ? (
                            <div className="space-y-2">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Skeleton className="w-8 h-8 rounded-full" />
                                            <div className="space-y-2">
                                                <Skeleton className="h-3 w-40" />
                                                <Skeleton className="h-3 w-24" />
                                            </div>
                                        </div>
                                        <Skeleton className="h-8 w-16 rounded-md" />
                                    </div>
                                ))}
                            </div>
                        ) : recentPackages.length > 0 ? (
                            recentPackages.map((req, idx) => (
                                <div
                                    key={req.id}
                                    style={{ transitionDelay: `${idx * 40}ms` }}
                                    className={`group flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg gap-3 transition-all duration-200 hover:bg-white dark:hover:bg-slate-700/50 hover:shadow-sm hover:-translate-y-[1px] ${pkgAnimReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div title="بكج" className="relative p-2 bg-primary/10 rounded-full text-primary transition-colors duration-200 group-hover:bg-primary/15 group-hover:text-primary group-hover:ring-1 group-hover:ring-primary/30">
                                            <PackageIcon size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold truncate">{req.title || `طلب بكج ${req.id}`}</p>
                                            <p className="text-xs text-slate-500">{req.employeeName || 'غير معروف'}</p>
                                        </div>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={() => navigate(`/admin-packages/${req.id}`)}>
                                        <Eye size={14} />
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-center text-slate-500 py-4">لا توجد طلبات بكج جديدة.</p>
                        )}
                    </CardContent>
                    <div className="p-3 border-t border-slate-200 dark:border-slate-600">
                        <Button variant="secondary" className="w-full" onClick={() => navigate('/admin-packages')}>عرض الكل</Button>
                    </div>
                </Card>
                {/* NEW: إدارة المشتريات - show latest from local storage */}
                <Card interactive className="flex flex-col">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <FileText size={20} className="text-nav-workflow" /> إدارة المشتريات
                        </CardTitle>
                        <span className={`text-sm font-bold text-nav-workflow`}>{purchaseItems.length}</span>
                    </CardHeader>
                    <CardContent className="flex-1 space-y-2">
                        {recentPurchases.length > 0 ? (
                            recentPurchases.map((p, idx) => (
                                <div
                                    key={p.id}
                                    style={{ transitionDelay: `${idx * 40}ms` }}
                                    className={`group flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg gap-3 transition-all duration-200 hover:bg-white dark:hover:bg-slate-700/50 hover:shadow-sm hover:-translate-y-[1px] ${listAnimReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div title="فاتورة" className="relative p-2 bg-primary/10 rounded-full text-primary transition-colors duration-200 group-hover:bg-primary/15 group-hover:text-primary group-hover:ring-1 group-hover:ring-primary/30">
                                            <FileText size={20} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold truncate">فاتورة #{p.invoiceNumber}</p>
                                            <p className="text-xs text-slate-500">{p.vendor || 'غير معروف'}{p.employeeName ? ` • ${p.employeeName}` : ''}</p>
                                        </div>
                                    </div>
                                    <Button size="sm" variant="ghost" onClick={() => navigate(`/`)}>
                                        <Eye size={14} />
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-center text-slate-500 py-4">لا توجد فواتير مسجلة بعد.</p>
                        )}
                    </CardContent>
                    <div className="p-3 border-t border-slate-200 dark:border-slate-600">
                        <Button variant="secondary" className="w-full" onClick={() => navigate('/')}>عرض الكل</Button>
                    </div>
                </Card>
            </div>

            <Card>
                <CardHeader className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CardTitle>أحدث التقارير الواردة</CardTitle>
                        <button
                            type="button"
                            className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/30 hover:bg-primary/15 transition-colors"
                            onClick={() => setRecentCollapsed(v => !v)}
                            title={recentCollapsed ? 'توسيع' : 'طيّ'}
                        >
                            <ChevronDown size={16} className={`${recentCollapsed ? '' : 'rotate-180'} transition-transform`} />
                        </button>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'all' ? 'ring-1 ring-slate-300 bg-slate-100 text-slate-700' : 'text-slate-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('all')}>الكل ({recentCounts.all})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'sales' ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'text-blue-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('sales')}>المبيعات ({recentCounts.sales})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'maintenance' ? 'bg-green-100 text-green-700 ring-1 ring-green-300' : 'text-green-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('maintenance')}>الصيانة ({recentCounts.maintenance})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'projects' ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300' : 'text-orange-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('projects')}>المشاريع ({recentCounts.projects})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'workflow' ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300' : 'text-purple-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('workflow')}>الاستيراد والتصدير ({recentCounts.workflow})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'package' ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300' : 'text-purple-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('package')}>البكجات ({recentCounts.package})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'purchase' ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300' : 'text-blue-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('purchase')}>المشتريات ({recentCounts.purchase})</Button>
                        <Button size="sm" variant="ghost" className={`${recentFilter === 'quotation' ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300' : 'text-orange-700'} transition-all active:scale-95 hover:-translate-y-[1px]`} onClick={() => setRecentFilter('quotation')}>عروض السعر ({recentCounts.quotation})</Button>
                    </div>
                </CardHeader>
                <CardContent className={`transition-all duration-300 ease-in-out overflow-hidden ${recentCollapsed ? 'max-h-0 opacity-0 invisible' : 'max-h-[1200px] opacity-100 visible'}`}>
                    {isSwitching ? (
                        <div className="space-y-3">
                            {[...Array(ITEMS_PER_PAGE)].map((_, i) => (
                                <div key={i} className="flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="w-8 h-8 rounded-full" />
                                        <div className="space-y-2">
                                            <Skeleton className="h-3 w-40" />
                                            <Skeleton className="h-3 w-24" />
                                        </div>
                                    </div>
                                    <Skeleton className="h-8 w-16 rounded-md" />
                                </div>
                            ))}
                        </div>
                    ) : paginatedUnified.length > 0 ? (
                        <div className={`space-y-3`}>
                            {paginatedUnified.map((item, idx) => {
                                const iconEl = item.kind === 'report' ? <FileText size={20} /> : item.kind === 'workflow' ? <Download size={20} /> : item.kind === 'package' ? <PackageIcon size={20} /> : item.kind === 'purchase' ? <FileText size={20} /> : <FileText size={20} />;
                                const viewPath = item.kind === 'report' ? `/reports/${item.id}` : item.kind === 'workflow' ? `/workflow/${item.id}` : item.kind === 'package' ? `/admin-packages/${item.id}` : item.kind === 'purchase' ? `/` : `/quotations/${item.id}`;
                                const hasUnreadNotes = item.kind === 'report' && !!user && (reports.find(r => r.id === item.id)?.adminNotes || []).some(note => {
                                    if (!note.readBy?.includes(user.id)) return true;
                                    return (note.replies || []).some(reply => !reply.readBy?.includes(user.id));
                                });

                                return (
                                    <div key={item.id} style={{ transitionDelay: `${idx * 40}ms` }} className={`group flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg gap-3 transition-all duration-200 hover:bg-white dark:hover:bg-slate-700/50 hover:shadow-sm hover:-translate-y-[1px] ${listAnimReady ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`}>
                                        <div className="flex items-center gap-3">
                                            <div title={item.kind === 'report' ? 'تقرير' : item.kind === 'workflow' ? 'سير عمل' : 'بكج'} className="relative p-2 bg-primary/10 rounded-full text-primary transition-colors duration-200 group-hover:bg-primary/15 group-hover:text-primary group-hover:ring-1 group-hover:ring-primary/30">
                                                {iconEl}
                                                {hasUnreadNotes && (
                                                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-semibold">{item.title}</p>
                                                <p className="text-xs text-slate-500">
                                                    {item.subtitle}
                                                    <span className="mx-1">•</span>
                                                    {timeAgo(item.date.toISOString())}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 self-end sm:self-center">
                                            <Button size="sm" variant="ghost" onClick={() => navigate(viewPath)} icon={<Eye size={14} />}>عرض</Button>
                                            {item.kind === 'report' && (
                                                <Button size="sm" variant="secondary" onClick={() => printReport(item.id)} icon={<Printer size={14} />}>طباعة</Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <EmptyState
                            icon={FileText}
                            title="لا توجد عناصر"
                            message="لا توجد عناصر ضمن الفلتر المحدد حالياً."
                        />
                    )}
                </CardContent>
                <CardFooter className={`flex justify-between items-center transition-all duration-300 ease-in-out overflow-hidden ${recentCollapsed ? 'max-h-0 opacity-0 invisible' : 'max-h-24 opacity-100 visible'}`}>
                    <span className="text-sm text-slate-500">عرض {paginatedUnified.length} من {recentUnifiedFiltered.length} عنصر</span>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </CardFooter>
            </Card>

        </div>
    );
};

const AdminDashboardScreen: React.FC = () => {
    const { designMode } = useAppContext();
    return designMode === 'modern' ? <ModernAdminDashboardScreen /> : <ClassicAdminDashboardScreen />;
};

export default AdminDashboardScreen;
